import { Box, SimpleGrid, Stack, Text } from '@mantine/core';
import { CardShell } from '../../atlas/cards/CardShell';
import { MetricRow } from '../../atlas/cards/MetricRow';
import { latest } from '../componentProjectMetricUtils';
import { ProgramParticipationPanel } from './ProgramParticipationPanel';
import {
  ASWP_PROGRAM_LABELS,
  ASWP_PROGRAM_ORDER,
  ASWP_PLACEMENT_RATE_GOAL_PCT,
  ASWP_TOTAL_PARTICIPATION_ALL_TIME_GOAL,
  formatAswpParticipationAllTimeGoalLabel,
  type AswpComponentProjectData,
} from './mapAswpComponentProjectData';

type AswpProgramParticipationCardProps = {
  data: AswpComponentProjectData;
};

const ALL_PROGRAMS_LABEL = 'All Programs';

function formatPlacementPct(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(1)}%`;
}

export function AswpProgramParticipationCard({ data }: AswpProgramParticipationCardProps) {
  const series = data.series;
  const cur = latest(series);
  const subtitle = cur?.quarter ?? 'No data';
  const allTimeTotals = data.allTime.totals;

  const participationAllTimeGoal =
    cur?.goalTargets?.totals?.participationAllTime ?? ASWP_TOTAL_PARTICIPATION_ALL_TIME_GOAL;
  const placementRateGoal =
    cur?.goalTargets?.totals?.placementRate ?? ASWP_PLACEMENT_RATE_GOAL_PCT;

  return (
    <CardShell title="Participation" subtitle={subtitle}>
      <Stack spacing="lg">
        <Box>
          <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginBottom: 10 }}>
            {ALL_PROGRAMS_LABEL}
          </Text>
          <MetricRow
            items={[
              {
                label: 'Participation (All-Time)',
                labelTooltip: `Total participants across all ASWP programs and all reported quarters (includes periods not shown). Grant target: ${formatAswpParticipationAllTimeGoalLabel(participationAllTimeGoal)} participants across all programs.`,
                value: (
                  <Text size="lg" weight={800}>
                    {allTimeTotals.participation}
                  </Text>
                ),
                sublabel: `Goal ${formatAswpParticipationAllTimeGoalLabel(participationAllTimeGoal)}`,
              },
              {
                label: 'Placement (All-Time)',
                labelTooltip:
                  'Total participants placed in jobs across all ASWP programs and all time (includes periods not shown).',
                value: (
                  <Text size="lg" weight={800}>
                    {allTimeTotals.placement}
                  </Text>
                ),
              },
              {
                label: 'Placement Rate (All-Time)',
                labelTooltip:
                  'Percent of all-time participants placed in jobs (placement ÷ participation). Grant target: 50% placement by end of Year 5.',
                value: (
                  <Text size="lg" weight={800}>
                    {formatPlacementPct(allTimeTotals.placementPct)}
                  </Text>
                ),
                sublabel: `Goal ${placementRateGoal}%`,
              },
            ]}
          />
        </Box>

        <Box>
          <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginBottom: 10 }}>
            By Program
          </Text>
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 900, cols: 1 }]}>
            {ASWP_PROGRAM_ORDER.map((programId) => {
              const programLabel = ASWP_PROGRAM_LABELS[programId];
              const allTime = data.allTime.programs[programId];

              return (
                <ProgramParticipationPanel
                  key={programId}
                  programLabel={programLabel}
                  allTime={allTime}
                  placementRateGoal={
                    cur?.goalTargets?.programs[programId]?.placementRate ?? placementRateGoal
                  }
                />
              );
            })}
          </SimpleGrid>
        </Box>
      </Stack>
    </CardShell>
  );
}
